import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { sortBy } from 'lodash';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components/macro';
import { WxDialog, WxLabel, WxPrice, WxText } from '../..';
import { VariantOrderItem } from '../../../hooks/api/queries/useVariantsByIds';
import { useCartContext } from '../../../hooks/context/useCartContext';
import { __ } from '../../../i18n/l10n';
import { IMAGE_RATIOS } from '../../../styles/imageRatios';
import { DesktopOnly } from '../../layout/nav/DesktopNavbar';
import { WImage } from '../../ui/image/Image';
import { RedLink } from '../order/OrderCard';
import { StyledFontAwesomeIcon } from './CartItem';

interface Props {
  item: VariantOrderItem;
  quantity: number;
}

export const CartItemDesktop = ({ item, quantity }: Props) => {
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState(false);
  const { incrementOne, removeItem, decrementOne } = useCartContext();
  const price = item.priceOverwrite ? item.priceOverwrite : item.price;

  return (
    <DesktopOnly>
      <WxDialog
        title={__('Confirm cancellation')}
        content={__('Are you sure you want to delete this item?')}
        open={deleteModal}
        setOpen={setDeleteModal}
        onClose={() => removeItem(item.variantId)}
      />
      <Row>
        <div>
          <ImageDiv onClick={() => navigate(`/product/${item.slug}`)}>
            <WImage
              image={
                item.localizedVariantImage ??
                item.defaultVariantImage ??
                item.localizedProductImage ??
                item.defaultProductImage
              }
              background
            />
          </ImageDiv>
        </div>
        <div>
          <Margin>
            <LabelLink>
              <WxLabel
                onClick={() => navigate(`/product/${item.slug}`)}
                hover
                label={item.label}
              />
            </LabelLink>
          </Margin>
          <RedLink onClick={() => setDeleteModal(true)}>{__('Delete')}</RedLink>
        </div>
        <div>
          <ul>
            {sortBy(item.attributes, 'keySorting')?.map((attr) => (
              <li key={`${item.variantId}-${attr.id}`}>
                <WxText text={attr.label} darkMode />
              </li>
            ))}
          </ul>
        </div>

        <CountWrapper>
          <StyledFontAwesomeIcon
            icon={faMinus}
            onClick={() => {
              if (quantity === 1) setDeleteModal(true);
              else decrementOne(item.variantId);
            }}
          />
          {quantity}
          <StyledFontAwesomeIcon
            icon={faPlus}
            onClick={() => incrementOne(item.variantId)}
            disabled={item.availableQuantity < quantity + 1}
          />
        </CountWrapper>

        <WxPrice
          price={price.milesAmount * quantity}
          priceBefore={
            item.priceOverwrite ? item.price?.milesAmount * quantity : undefined
          }
          dark
          noPadding
          background={false}
        />
      </Row>
    </DesktopOnly>
  );
};

const Row = styled.div`
  display: flex;
  margin-top: 0.75rem;
  > * {
    flex: 1;
  }

  > :first-child {
    div {
      padding-right: 3.75rem;
    }
  }

  > :last-child {
    display: flex;
    justify-content: flex-end;
  }
`;

const CountWrapper = styled.div`
  display: flex;
  gap: 0.8125rem;
  margin-right: 0.5rem;
  color: ${(props) => props.theme.colors.white};
  font-size: 0.75rem;
`;

const Margin = styled.div`
  margin-bottom: 1.25rem;

  p {
    overflow: hidden;
    max-width: 20ch;
  }
`;

const ImageDiv = styled.div`
  cursor: pointer;
  img {
    aspect-ratio: ${IMAGE_RATIOS.PRODUCT_LIST};
  }
`;

const LabelLink = styled.div`
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
`;
